// frontend/src/features/inventory/pages/PrintBatchPage.tsx
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import QRCode from "qrcode";
import {
  useV1InventoryLotsList,
  useV1InventoryLabelPrintSettingsList,
} from "@/api/generated/v1/v1";

function SingleLabel({ lot, kind, widthMm, heightMm }: { lot: any, kind: string, widthMm: number, heightMm: number }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!lot?.lot_number || !canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, lot.lot_number, { width: kind === "full" ? 120 : 75, margin: 1 });
  }, [lot?.lot_number, kind]);

  return (
    <div className="label-box" dir="rtl" style={{
      width: `${widthMm}mm`,
      height: `${heightMm}mm`,
      border: "1px dashed #ccc",
      padding: "2mm",
      boxSizing: "border-box",
      fontFamily: "Tahoma, sans-serif",
      display: "flex",
      flexDirection: kind === "full" ? "row" : "column",
      alignItems: "center",
      justifyContent: kind === "full" ? "flex-start" : "center",
      gap: "2mm",
      pageBreakAfter: "always",
      overflow: "hidden",
    }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1mm", width: kind === "full" ? "32mm" : "20mm" }}>
        <canvas ref={canvasRef} />
        <div style={{ fontWeight: "bold", fontSize: kind === "full" ? "3mm" : "2.5mm", textAlign: "center", wordWrap: "break-word", width: "100%" }} dir="ltr">
          {lot.lot_number}
        </div>
      </div>

      {kind === "compact" && (
        <div style={{ fontSize: "2.5mm", textAlign: "center", lineHeight: 1.2 }}>
          {lot.material_code || "-"} — {lot.material_name || "-"}
        </div>
      )}

      {kind === "full" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5mm", textAlign: "right", flex: 1 }}>
          <div style={{ fontSize: "5mm", fontWeight: "bold", lineHeight: 1.2 }}>
            {lot.material_name || "-"}
          </div>
          <div style={{ fontSize: "3mm", color: "#444" }}>
            کد: {lot.material_code || "-"}
          </div>
          <div style={{ fontSize: "3mm" }}>شماره بارنامه: {lot.reference_doc || "—"}</div>
          <div style={{ fontSize: "3mm" }}>
            مقدار: <span dir="ltr">{lot.received_qty} {lot.stock_uom_symbol}</span>
          </div>
          <div style={{ fontSize: "3mm" }}>
            ضریب: <span dir="ltr">{lot.effective_numerator} ({lot.production_uom_symbol}) = {lot.effective_denominator} ({lot.stock_uom_symbol})</span>
          </div>
          <div style={{ fontSize: "3mm" }}>تاریخ ورود: <span dir="ltr">{lot.received_date_jalali}</span></div>
        </div>
      )}
    </div>
  );
}

export function PrintBatchPage() {
  const [searchParams] = useSearchParams();
  const refDoc = searchParams.get("ref");
  const kind = (searchParams.get("kind") as "compact" | "full") || "compact";

  const { data: lotsData, isLoading, isError, error } = useV1InventoryLotsList(
    // We pass source_transaction__reference_doc using `as any` because it's not typed yet in generated API
    { source_transaction__reference_doc: refDoc } as any,
    { query: { enabled: !!refDoc } }
  );

  const lots = lotsData?.results ?? [];

  const { data: settingsList } = useV1InventoryLabelPrintSettingsList(undefined, { query: {} });

  const settings = (settingsList?.results ?? []).find((s: any) => s.template_kind === kind);
  const widthMm = settings ? Number(settings.paper_width_mm) : kind === "compact" ? 50 : 100;
  const heightMm = settings ? Number(settings.paper_height_mm) : kind === "compact" ? 30 : 150;

  useEffect(() => {
    if (lots.length > 0 && !isError) {
      // Auto-print delay
      const timer = setTimeout(() => window.print(), 800);
      return () => clearTimeout(timer);
    }
  }, [lots, isError]);

  if (!refDoc) {
     return <div style={{ padding: "2rem" }}>شماره سند مرجع وارد نشده است.</div>;
  }

  if (isLoading) {
    return <div style={{ padding: "2rem" }}>در حال بارگذاری لیست پالت‌ها...</div>;
  }

  if (isError || error) {
    const errObj: any = error;
    return (
      <div style={{ padding: "2rem", color: "red" }} dir="ltr">
        <h3>Error loading lots:</h3>
        <pre>{JSON.stringify(errObj?.response?.data || errObj?.message, null, 2)}</pre>
      </div>
    );
  }

  if (lots.length === 0) {
    return <div style={{ padding: "2rem" }}>هیچ پالتی برای این سند مرجع یافت نشد.</div>;
  }

  return (
    <>
      <style>{`
        @media print {
          @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
          html, body, #root, main, .container {
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
            overflow: visible !important;
          }
          .no-print { display: none !important; }
          /* Hide React Query Devtools */
          [class*="tsqd-"], [class*="ReactQueryDevtools"] { display: none !important; }
          .label-box { border: none !important; margin: 0; }
          .print-container { display: block !important; }
        }
        .print-container {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
      `}</style>

      <div className="no-print" style={{ padding: "1rem", textAlign: "center" }}>
        <button className="btn btn-primary" onClick={() => window.print()}>
          چاپ همه ({lots.length} لیبل)
        </button>
      </div>

      <div className="print-container">
        {lots.map((lot: any) => (
          <SingleLabel key={lot.id} lot={lot} kind={kind} widthMm={widthMm} heightMm={heightMm} />
        ))}
      </div>
    </>
  );
}
